import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/context/AuthContext';
import { getUserDisplayName, getUserInitials } from '@/utils/user-display';
import { StatCard } from '@/pages/Organization/Analytics/components/StatCard';
import { adminApi } from '@/utils/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';

type CoupleRow = {
    id: string;
    partnerAName?: string;
    partnerBName?: string;
    partnerAPhone?: string;
    partnerBPhone?: string;
    status?: string;
    invitedAt?: string;
};

type OrgOption = {
    id: string;
    name: string;
};

const INITIAL_FORM = {
    organizationId: '',
    personOneFirstName: '',
    personOneLastName: '',
    personOneEmail: '',
    personOnePhoneNumber: '',
    personOneDateOfBirth: '',
    personOneGender: '' as '' | 'male' | 'female',
    personTwoFirstName: '',
    personTwoLastName: '',
    personTwoEmail: '',
    personTwoPhoneNumber: '',
    personTwoDateOfBirth: '',
    personTwoGender: '' as '' | 'male' | 'female',
};

const INPUT_CLS =
    'w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:ring-1 focus:ring-[#FF6600] focus:outline-none';

export default function AdminCouplesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const userName = getUserDisplayName(user);
    const userInitials = getUserInitials(user);
    const [couples, setCouples] = useState<CoupleRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [resendingId, setResendingId] = useState<string | null>(null);
    const [inviteForm, setInviteForm] = useState({ ...INITIAL_FORM });
    const [organizations, setOrganizations] = useState<OrgOption[]>([]);

    const fetchCouples = async () => {
        try {
            setError(null);
            setLoading(true);
            const res = await adminApi.couplesList({ page: 1, limit: 20 });
            const rows =
                (res as { data?: { data?: CoupleRow[] } }).data?.data ||
                (res as { data?: CoupleRow[] }).data ||
                [];
            setCouples(Array.isArray(rows) ? rows : []);
        } catch (err) {
            setError((err as Error)?.message || 'Failed to load couples');
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await adminApi.organizationStats();
            const data =
                (res as unknown as { data?: OrgOption[] }).data ||
                (res as unknown as { data?: { data?: OrgOption[] } }).data?.data ||
                [];
            setOrganizations(Array.isArray(data) ? data : []);
        } catch {
            // Non-critical — org dropdown will just be empty
        }
    };

    useEffect(() => {
        void fetchCouples();
        void fetchOrganizations();
    }, []);

    const totals = useMemo(() => {
        const total = couples.length;
        const invited = couples.filter((c) => c.status === 'invited').length;
        const completed = couples.filter((c) => c.status === 'completed').length;
        return { total, invited, completed };
    }, [couples]);

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setInviteError(null);
        setInviteSuccess(null);

        if (!inviteForm.organizationId) {
            setInviteError('Please select an organization.');
            return;
        }
        if (!inviteForm.personOneGender || !inviteForm.personTwoGender) {
            setInviteError('Please select gender for both persons.');
            return;
        }

        setInviteLoading(true);
        try {
            // Build payload, omitting empty optional fields
            const payload: Record<string, unknown> = {
                organizationId: inviteForm.organizationId,
                personOneFirstName: inviteForm.personOneFirstName,
                personOneLastName: inviteForm.personOneLastName,
                personOneEmail: inviteForm.personOneEmail,
                personOnePhoneNumber: inviteForm.personOnePhoneNumber,
                personOneGender: inviteForm.personOneGender,
                personTwoFirstName: inviteForm.personTwoFirstName,
                personTwoLastName: inviteForm.personTwoLastName,
                personTwoEmail: inviteForm.personTwoEmail,
                personTwoPhoneNumber: inviteForm.personTwoPhoneNumber,
                personTwoGender: inviteForm.personTwoGender,
            };
            if (inviteForm.personOneDateOfBirth) payload.personOneDateOfBirth = inviteForm.personOneDateOfBirth;
            if (inviteForm.personTwoDateOfBirth) payload.personTwoDateOfBirth = inviteForm.personTwoDateOfBirth;

            await adminApi.couplesInvite(payload);
            setInviteSuccess('Couple invited successfully. WhatsApp invitations have been sent.');
            setInviteForm({ ...INITIAL_FORM });
            await fetchCouples();
            setTimeout(() => {
                setAddOpen(false);
                setInviteSuccess(null);
            }, 2000);
        } catch (err) {
            const message = (err as Error)?.message || 'Unable to invite couple';
            setInviteError(message);
        } finally {
            setInviteLoading(false);
        }
    };

    const handleResend = async (id: string) => {
        setResendingId(id);
        setError(null);
        try {
            await adminApi.couplesResendInvite(id);
            await fetchCouples();
        } catch (err) {
            setError((err as Error)?.message || 'Unable to resend invite');
        } finally {
            setResendingId(null);
        }
    };

    const updateField = (field: keyof typeof INITIAL_FORM, value: string) => {
        setInviteForm((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div className='min-h-screen bg-[#edeff3]'>
            <div className='p-4 md:p-6 lg:p-8'>
                <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
                    {/* Header */}
                    <div className='flex items-center justify-between'>
                        <div>
                            <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Couples</h1>
                            <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>
                                Invite couples, resend links, and track onboarding.
                            </p>
                        </div>
                        <div className='flex items-center gap-3'>
                            <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-sm'>
                                {userInitials}
                            </div>
                            <span className='text-[24px] font-bold text-[#353535] hidden sm:inline'>{userName}</span>
                        </div>
                    </div>

                    {/* Stat Cards */}
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                        <StatCard title='Total Couples' value={totals.total.toLocaleString()} subtitle='All time' trend='' />
                        <StatCard
                            title='Invited'
                            value={totals.invited.toLocaleString()}
                            subtitle='Pending onboarding'
                            trend=''
                        />
                        <StatCard
                            title='Completed'
                            value={totals.completed.toLocaleString()}
                            subtitle='Finished onboarding'
                            trend=''
                        />
                    </div>

                    {/* Couple Invites Section */}
                    <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4'>
                        <div className='flex items-center justify-between mb-4'>
                            <h3 className='text-[18px] font-bold text-[#151515]'>Couple Invites</h3>
                            <Sheet open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) { setInviteError(null); setInviteSuccess(null); } }}>
                                <SheetTrigger asChild>
                                    <Button className='bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold rounded-[8px]'>
                                        Invite Couple
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side='right' className='w-full sm:w-[480px] md:w-[560px]'>
                                    <SheetHeader>
                                        <SheetTitle>Invite Couple</SheetTitle>
                                    </SheetHeader>
                                    <form onSubmit={handleInvite} className='mt-4 space-y-4 overflow-y-auto max-h-[80vh] pr-1'>
                                        <p className='text-xs text-gray-600'>
                                            Both persons will receive a WhatsApp invitation to complete their assessment.
                                        </p>

                                        {/* Organization */}
                                        <div>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                Organization <span className='text-red-500'>*</span>
                                            </label>
                                            <select
                                                className={INPUT_CLS}
                                                required
                                                value={inviteForm.organizationId}
                                                onChange={(e) => updateField('organizationId', e.target.value)}
                                            >
                                                <option value=''>Select organization</option>
                                                {organizations.map((org) => (
                                                    <option key={org.id} value={org.id}>
                                                        {org.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Person One */}
                                        <div className='space-y-3'>
                                            <h4 className='text-sm font-bold text-[#151515] border-b border-[#eaecf0] pb-2'>
                                                Person One
                                            </h4>
                                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        First Name <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='John'
                                                        value={inviteForm.personOneFirstName}
                                                        onChange={(e) => updateField('personOneFirstName', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Last Name <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='Doe'
                                                        value={inviteForm.personOneLastName}
                                                        onChange={(e) => updateField('personOneLastName', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Email <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        type='email'
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='john@example.com'
                                                        value={inviteForm.personOneEmail}
                                                        onChange={(e) => updateField('personOneEmail', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Phone (E.164) <span className='text-red-500'>*</span>
                                                    </label>
                                                    <PhoneNumberInput
                                                        value={inviteForm.personOnePhoneNumber}
                                                        onChange={(value) => updateField('personOnePhoneNumber', value)}
                                                        placeholder='+2348012345678'
                                                        required
                                                        containerClassName='border-gray-300'
                                                        inputClassName='py-0'
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Gender <span className='text-red-500'>*</span>
                                                    </label>
                                                    <select
                                                        className={INPUT_CLS}
                                                        required
                                                        value={inviteForm.personOneGender}
                                                        onChange={(e) =>
                                                            updateField('personOneGender', e.target.value)
                                                        }
                                                    >
                                                        <option value=''>Select gender</option>
                                                        <option value='male'>Male</option>
                                                        <option value='female'>Female</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Date of Birth
                                                    </label>
                                                    <input
                                                        type='date'
                                                        className={INPUT_CLS}
                                                        value={inviteForm.personOneDateOfBirth}
                                                        onChange={(e) => updateField('personOneDateOfBirth', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Person Two */}
                                        <div className='space-y-3'>
                                            <h4 className='text-sm font-bold text-[#151515] border-b border-[#eaecf0] pb-2'>
                                                Person Two
                                            </h4>
                                            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        First Name <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='Jane'
                                                        value={inviteForm.personTwoFirstName}
                                                        onChange={(e) => updateField('personTwoFirstName', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Last Name <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='Doe'
                                                        value={inviteForm.personTwoLastName}
                                                        onChange={(e) => updateField('personTwoLastName', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Email <span className='text-red-500'>*</span>
                                                    </label>
                                                    <input
                                                        type='email'
                                                        className={INPUT_CLS}
                                                        required
                                                        placeholder='jane@example.com'
                                                        value={inviteForm.personTwoEmail}
                                                        onChange={(e) => updateField('personTwoEmail', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Phone (E.164) <span className='text-red-500'>*</span>
                                                    </label>
                                                    <PhoneNumberInput
                                                        value={inviteForm.personTwoPhoneNumber}
                                                        onChange={(value) => updateField('personTwoPhoneNumber', value)}
                                                        placeholder='+2348087654321'
                                                        required
                                                        containerClassName='border-gray-300'
                                                        inputClassName='py-0'
                                                    />
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Gender <span className='text-red-500'>*</span>
                                                    </label>
                                                    <select
                                                        className={INPUT_CLS}
                                                        required
                                                        value={inviteForm.personTwoGender}
                                                        onChange={(e) =>
                                                            updateField('personTwoGender', e.target.value)
                                                        }
                                                    >
                                                        <option value=''>Select gender</option>
                                                        <option value='male'>Male</option>
                                                        <option value='female'>Female</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                                        Date of Birth
                                                    </label>
                                                    <input
                                                        type='date'
                                                        className={INPUT_CLS}
                                                        value={inviteForm.personTwoDateOfBirth}
                                                        onChange={(e) => updateField('personTwoDateOfBirth', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Feedback */}
                                        {(inviteError || inviteSuccess) && (
                                            <div
                                                className={`text-sm rounded-lg px-4 py-3 ${
                                                    inviteError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                                }`}
                                            >
                                                {inviteError || inviteSuccess}
                                            </div>
                                        )}

                                        <Button
                                            type='submit'
                                            disabled={inviteLoading}
                                            className='w-full bg-[#151515] hover:bg-[#2a2a2a] font-bold rounded-[8px]'
                                        >
                                            {inviteLoading ? 'Sending Invitation...' : 'Send Invite'}
                                        </Button>
                                    </form>
                                </SheetContent>
                            </Sheet>
                        </div>

                        {error && <p className='text-sm text-red-600 mb-3'>{error}</p>}

                        <div className='overflow-x-auto'>
                            <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px]'>
                                <div className='grid grid-cols-6 gap-2 px-4 py-2 text-start'>
                                    <span className='font-medium text-[14px] text-[#475467]'>Partner A</span>
                                    <span className='font-medium text-[14px] text-[#475467]'>Partner B</span>
                                    <span className='font-medium text-[14px] text-[#475467]'>Status</span>
                                    <span className='font-medium text-[14px] text-[#475467]'>Invited</span>
                                    <span className='font-medium text-[14px] text-[#475467]'>Phones</span>
                                    <span className='font-medium text-[14px] text-[#475467] text-right'>Actions</span>
                                </div>
                                {loading && <p className='text-[14px] text-[#101828] p-4'>Loading couples...</p>}
                                {!loading && couples.length === 0 && (
                                    <p className='text-[14px] text-[#101828] p-4'>No couples found.</p>
                                )}
                                {!loading &&
                                    couples.map((c) => (
                                        <div
                                            key={c.id}
                                            className='grid grid-cols-6 gap-2 px-4 py-3 text-[14px] text-[#101828] border-b border-[#eaecf0] hover:bg-[#f9fafb] items-center text-start'
                                        >
                                            <span className='font-semibold'>{c.partnerAName || 'Partner A'}</span>
                                            <span>{c.partnerBName || 'Partner B'}</span>
                                            <span className='capitalize'>{c.status || 'invited'}</span>
                                            <span>
                                                {c.invitedAt ? new Date(c.invitedAt).toLocaleDateString() : '—'}
                                            </span>
                                            <span className='text-xs text-[#475467]'>
                                                {c.partnerAPhone || '—'} / {c.partnerBPhone || '—'}
                                            </span>
                                            <div className='flex justify-end gap-2 text-xs'>
                                                <button
                                                    type='button'
                                                    className='px-3 py-1 rounded-[8px] bg-[rgba(237,239,243,0.5)] text-[#101828] hover:bg-[#e0e2e7]'
                                                    onClick={() => navigate(`/admin/couples/${c.id}`)}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    type='button'
                                                    className='px-3 py-1 rounded-[8px] bg-orange-50 text-orange-600 hover:bg-orange-100 disabled:opacity-50'
                                                    onClick={() => handleResend(c.id)}
                                                    disabled={resendingId === c.id}
                                                >
                                                    {resendingId === c.id ? 'Resending...' : 'Resend Invite'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
